<template>
  <div class="msds-prev">
    <!-- 상단 바 -->
    <div class="msds-prev__bar">
      <v-btn icon small class="mr-1" @click="onBack"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <strong class="msds-prev__title">{{ docType }} 미리보기</strong>
      <span class="msds-prev__divider">|</span>
      <span class="msds-prev__chip">{{ lotNo }}</span>
      <span class="msds-prev__chip">{{ code }}</span>
      <span v-if="sections.length" class="msds-prev__divider">|</span>
      <span v-if="editing" class="msds-prev__editbadge"><v-icon left x-small>mdi-pencil</v-icon>편집 중 ({{ changeCount }}개 변경)</span>
      <!-- ===== [SECNAV] 섹션 바로가기 — 제목과 같은 줄, 좁으면 가로 스크롤 ===== -->
      <div v-if="sections.length" class="msds-prev__nav">
        <span class="msds-prev__navlabel">목차</span>
        <button v-for="(s, i) in sections" :key="i" type="button" class="msds-prev__navbtn" :class="{ 'is-active': activeSection === i }" @click="goSection(i)">
          <span class="msds-prev__navnum">{{ s.num }}</span
          >{{ s.label }}
        </button>
      </div>
      <v-spacer />
      <template v-if="viewOnly">
        <v-btn depressed outlined class="msds-prev__action mr-2" :disabled="loading || !!error" @click="$emit('download')"> <v-icon left small>mdi-download</v-icon>다운로드 </v-btn>
        <v-btn depressed color="primary" class="msds-prev__action" @click="onBack"> <v-icon left small>mdi-close</v-icon>닫기 </v-btn>
      </template>
      <template v-else-if="!editing">
        <v-btn depressed outlined class="msds-prev__action mr-2" :disabled="loading || !!error" @click="$emit('requestEdit')"> <v-icon left small>mdi-pencil</v-icon>수정 </v-btn>
        <v-btn depressed color="primary" class="msds-prev__action" :loading="publishing" :disabled="loading || !!error" @click="$emit('publish')"> <v-icon left small>mdi-content-save</v-icon>저장 </v-btn>
      </template>
      <template v-else>
        <v-btn depressed outlined class="msds-prev__action mr-2" :disabled="publishing" @click="cancelEdit"> <v-icon left small>mdi-close</v-icon>취소 </v-btn>
        <v-btn depressed color="primary" class="msds-prev__action" :loading="publishing" @click="saveEdit"> <v-icon left small>mdi-content-save</v-icon>저장 </v-btn>
      </template>
    </div>

    <!-- 수정 사유 바 (편집 중에만) -->
    <div v-if="editing" class="msds-prev__reason" :class="{ 'msds-prev__reason--err': reasonError }">
      <span class="msds-prev__reason-label">수정 사유<span class="msds-prev__req">*</span></span>
      <input v-model="editReason" class="msds-prev__reason-input" type="text" placeholder="수정 사유를 입력해주세요" @input="reasonError = false" />
    </div>

    <!-- 본문 — [CONDPANEL] 좌측 판정조건 패널 + 문서 -->
    <div class="msds-prev__main">
      <!-- ===== [CONDPANEL] 시작 — 되돌릴 때 이 aside 블록 삭제 ===== -->
      <aside v-if="docType === 'MSDS'" class="msds-prev__side" :class="{ 'is-collapsed': sideCollapsed }">
        <button type="button" class="msds-prev__sidetoggle" :title="sideCollapsed ? '판정 조건 펼치기' : '판정 조건 접기'" @click="sideCollapsed = !sideCollapsed">
          <v-icon x-small>{{ sideCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left' }}</v-icon>
          <span v-if="!sideCollapsed" class="msds-prev__sidetitle">판정 조건</span>
        </button>
        <div v-show="!sideCollapsed" class="msds-prev__sidebody">
          <div v-if="condLoading" class="msds-prev__sidemsg">불러오는 중…</div>
          <template v-else-if="cond">
            <!-- 업체 -->
            <div class="msds-prev__sgroup">
              <div class="msds-prev__slabel">수령업체</div>
              <div class="msds-prev__sval">{{ cond.recipientCompany || '—' }}</div>
              <div class="msds-prev__tags">
                <span class="msds-prev__tag" :class="cond.lg ? 'on' : 'off'">LG {{ cond.lg ? 'O' : 'X' }}</span>
                <span class="msds-prev__tag" :class="cond.big4 ? 'on' : 'off'">대표4사 {{ cond.big4 ? 'O' : 'X' }}</span>
              </div>
            </div>
            <!-- 코드 -->
            <div class="msds-prev__sgroup">
              <div class="msds-prev__srow">
                <span>코드그레이드</span><b>{{ cond.code || '—' }}</b>
              </div>
              <div class="msds-prev__srow">
                <span>요청 자재코드</span><b>{{ cond.requestCode || '—' }}</b>
              </div>
            </div>
            <!-- 룰 성분 -->
            <div class="msds-prev__sgroup">
              <div class="msds-prev__slabel">분기 성분</div>
              <div v-for="f in cond.componentFlags || []" :key="f.code" class="msds-prev__flag" :class="{ on: f.applies, has: f.present && !f.applies }">
                <span class="msds-prev__flagcode">{{ f.code }}</span>
                <span class="msds-prev__flagstate">{{ f.present ? '포함' : '미포함' }}</span>
                <span v-if="f.present && diffName(f)" class="msds-prev__flagname">({{ f.compName }})</span>
                <span v-if="f.present && !f.applies" class="msds-prev__flagnote">업체 조건 불일치 — 미적용</span>
                <span v-else-if="f.applies" class="msds-prev__flagnote">{{ f.effect }}</span>
              </div>
            </div>
            <!-- 조성 -->
            <div class="msds-prev__sgroup">
              <div class="msds-prev__slabel">조성 ({{ (cond.components || []).length }})</div>
              <div v-for="(c, i) in cond.components || []" :key="i" class="msds-prev__srow">
                <span :title="c.rmav && c.rmav !== c.name ? '사내통용 원료명칭: ' + c.rmav : ''"
                  >{{ c.name }}<span v-if="diffReq(c)" class="msds-prev__flagname"> ({{ c.reqName }})</span><em v-if="!c.registered" class="msds-prev__warn" title="Subs_Index 미등록">!</em></span
                >
                <b>{{ c.pct }}%</b>
              </div>
            </div>
          </template>
          <div v-else class="msds-prev__sidemsg">판정 조건을 불러오지 못했습니다.</div>
        </div>
      </aside>
      <!-- ===== [CONDPANEL] 끝 ===== -->

      <div class="msds-prev__body">
        <div v-if="loading" class="msds-prev__center">
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="mt-3">{{ docType }} 문서를 생성하는 중입니다…</p>
        </div>
        <div v-else-if="error" class="msds-prev__center">
          <v-icon color="error" size="40">mdi-alert-circle</v-icon>
          <p class="mt-3">{{ error }}</p>
          <v-btn small depressed @click="fetchHtml">다시 시도</v-btn>
        </div>
        <iframe v-else ref="frame" class="msds-prev__paper" :srcdoc="html" scrolling="no" @load="fitHeight"></iframe>
        <!-- [SECNAV] 마지막 섹션(16번)도 맨 위까지 올라오도록 하단 여유 공간 — 되돌릴 때 이 1줄 삭제 -->
        <div v-if="sections.length" class="msds-prev__tail"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMsdsAutoPreviewHtml, getMsdsCurrentPreviewHtml, getLocAutoPreviewHtml, getLocCurrentPreviewHtml, msdsConditions } from 'api/sample/sample';

// 편집 모드에서 iframe 내부에 주입할 스타일(편집 가능 셀 표시/변경 셀 강조).
// 수정 셀은 background 로 노랗게(td/table-cell 에서 확실히 렌더됨). contentEditable 이 이 배경을 인라인 style 로
// 흘릴 수 있으나, 저장 시 buildEditedHtml 이 인라인 background 를 전부 제거하므로 저장HTML·PDF 는 깨끗하게 유지됨.
const EDIT_STYLE = '[data-ef]:hover{outline:1px dashed #f0a020;outline-offset:-1px;}' + '[data-efc="1"]{background:#fff3b0;}' + '[data-efa="1"]{outline:2px solid #e69500;outline-offset:-2px;}' + 'img{cursor:default;}';

export default {
  name: 'MsdsPreview',
  props: {
    lotNo: { type: String, default: '' },
    code: { type: String, default: '' },
    docType: { type: String, default: 'MSDS' }, // MSDS | LOC — 미리보기/편집 흐름은 동일, 호출 API만 다름
    publishing: { type: Boolean, default: false },
    startInEdit: { type: Boolean, default: false }, // 열자마자 편집모드로 시작(사유 모달 경유)
    initialReason: { type: String, default: '' }, // 사유 모달에서 받은 수정 사유
    preferCurrent: { type: Boolean, default: false }, // true=발행본(S3 저장 HTML+수정하이라이트), false=마스터 자동생성
    viewOnly: { type: Boolean, default: false }, // 발행문서 클릭 열람 — 수정/저장 없이 다운로드+닫기만
  },
  data() {
    return {
      html: '',
      loading: false,
      error: '',
      editing: false,
      changeCount: 0,
      editReason: '',
      reasonError: false,
      cond: null, // [CONDPANEL] 판정 조건 요약
      condLoading: false, // [CONDPANEL]
      sideCollapsed: false, // [CONDPANEL] 좌측 패널 접힘
      sections: [], // [SECNAV] 섹션 바로가기 목록 {num, label}
      activeSection: -1, // [SECNAV] 현재 보고 있는 섹션 index
    };
  },
  methods: {
    async fetchHtml() {
      this.loading = true;
      this.error = '';
      const isLoc = this.docType === 'LOC';
      try {
        // 발행본 있음(preferCurrent) → 저장 HTML+수정 하이라이트 로드 / 없음 → 마스터에서 자동생성
        let res;
        if (this.preferCurrent) {
          res = isLoc ? await getLocCurrentPreviewHtml(this.lotNo) : await getMsdsCurrentPreviewHtml(this.lotNo);
        } else {
          res = isLoc ? await getLocAutoPreviewHtml(this.lotNo) : await getMsdsAutoPreviewHtml(this.lotNo);
        }
        this.html = res.data;
      } catch (e) {
        this.error = `${this.docType} 미리보기를 불러오지 못했습니다. 마스터 데이터를 확인해주세요.`;
      } finally {
        this.loading = false;
      }
    },
    // ===== [CONDPANEL] 시작 — 되돌릴 때 이 메서드 삭제 =====
    // 좌측 패널용 판정 조건 조회 (MSDS 전용, 실패해도 미리보기에는 영향 없음)
    async fetchConditions() {
      if (this.docType !== 'MSDS') return;
      this.condLoading = true;
      try {
        const res = await msdsConditions(this.lotNo);
        const d = (res.data && res.data.data) || null;
        this.cond = d && Object.keys(d).length ? d : null;
      } catch (e) {
        this.cond = null;
      } finally {
        this.condLoading = false;
      }
    },
    // 조성 성분: 납품일보 명칭이 실제조성 명칭과 다를 때만 납품일보 명칭을 괄호로 병기
    diffReq(c) {
      const a = (c.reqName || '').trim().toLowerCase();
      const b = (c.name || '').trim().toLowerCase();
      return !!a && a !== b;
    },
    // 실제조성 성분명이 사내통용 명칭과 다를 때만 괄호로 표기
    diffName(f) {
      const a = (f.compName || '').trim().toLowerCase();
      const b = (f.rmav || '').trim().toLowerCase();
      return !!a && a !== b;
    },
    // ===== [CONDPANEL] 끝 =====

    // iframe 높이를 문서 전체 높이에 맞춤(내부 스크롤 없이 페이지 스크롤)
    fitHeight() {
      const f = this.$refs.frame;
      if (!f || !f.contentDocument) return;
      const h = f.contentDocument.documentElement.scrollHeight;
      if (h) f.style.height = h + 'px';
      this.fitViewport(); // [FIXHEAD]
      this.buildSections(); // [SECNAV]
      this.tryStartEdit();
    },
    // ===== [FIXHEAD] 시작 — 되돌릴 때 이 메서드 삭제 =====
    // 미리보기 영역 높이를 '화면 아래 남은 만큼'으로 고정 → 상단바/섹션바는 붙박이, 본문(iframe)만 스크롤.
    // 헤더·메뉴·탭 높이가 화면마다 달라서 상수 대신 실제 위치(top)를 재서 계산한다.
    fitViewport() {
      if (!this.$el || !this.$el.getBoundingClientRect) return;
      const top = this.$el.getBoundingClientRect().top + (window.pageYOffset ? 0 : 0);
      const h = window.innerHeight - top;
      if (h > 200) this.$el.style.height = h + 'px';
    },
    // ===== [FIXHEAD] 끝 =====

    // ===== [SECNAV] 시작 — 되돌릴 때 이 메서드 5개(buildSections~onDocScroll) 삭제 =====
    // 문서 안의 섹션 헤더(div.sec: "1. 화학제품과 회사에 관한 정보")를 읽어 바로가기 목록 생성.
    // 번호는 그대로 쓰고, 라벨은 바에 들어가도록 짧은 이름으로 치환(번호 매칭 실패 시 원문 사용).
    buildSections() {
      const doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc) return;
      const SHORT = {
        1: '제품·회사',
        2: '유해성·위험성',
        3: '구성성분',
        4: '응급조치',
        5: '폭발·화재',
        6: '누출 대처',
        7: '취급·저장',
        8: '노출방지',
        9: '물리화학',
        10: '안정성',
        11: '독성',
        12: '환경영향',
        13: '폐기',
        14: '운송정보',
        15: '법적규제',
        16: '기타',
      };
      const list = [];
      doc.querySelectorAll('.sec').forEach(el => {
        const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
        const m = t.match(/^(\d+)\s*\.\s*(.*)$/);
        const num = m ? m[1] : '';
        const rest = m ? m[2] : t;
        list.push({ num, label: (num && SHORT[Number(num)]) || rest });
      });
      this.sections = list;
      this.activeSection = list.length ? 0 : -1;
      // 스크롤에 따라 현재 섹션 하이라이트
      const sc = this.scrollContainer();
      if (sc && this._scEl !== sc) {
        if (this._scEl) this._scEl.removeEventListener('scroll', this.onDocScroll);
        sc.addEventListener('scroll', this.onDocScroll, { passive: true });
        this._scEl = sc;
      }
    },
    // 실제 스크롤 주체를 찾는다. [FIXHEAD] 이후에는 본문(.msds-prev__body)이 스크롤 주체이므로 이것을 먼저 본다.
    scrollContainer() {
      const body = this.$el && this.$el.querySelector('.msds-prev__body');
      if (body && window.getComputedStyle(body).overflowY === 'auto') return body;
      let el = this.$el && this.$el.parentElement;
      while (el && el !== document.body) {
        const ov = window.getComputedStyle(el).overflowY;
        if ((ov === 'auto' || ov === 'scroll') && el.scrollHeight > el.clientHeight + 4) return el;
        el = el.parentElement;
      }
      return window;
    },
    // 섹션 헤더의 화면상 위치(스크롤 컨테이너 기준 top) — iframe 은 내부 스크롤이 없어 rect 로 계산 가능
    sectionTops() {
      const f = this.$refs.frame;
      const doc = f && f.contentDocument;
      if (!doc) return [];
      const fTop = f.getBoundingClientRect().top;
      return Array.prototype.map.call(doc.querySelectorAll('.sec'), el => fTop + el.getBoundingClientRect().top);
    },
    // 바 클릭 → 해당 섹션으로 스크롤(바 높이만큼 여유를 둠)
    goSection(i) {
      const tops = this.sectionTops();
      if (!tops.length || tops[i] === undefined) return;
      const sc = this.scrollContainer();
      const navH = (this.$el.querySelector('.msds-prev__nav') || { offsetHeight: 0 }).offsetHeight;
      if (sc === window) window.scrollTo({ top: window.pageYOffset + tops[i] - navH - 12, behavior: 'smooth' });
      else sc.scrollTo({ top: sc.scrollTop + tops[i] - sc.getBoundingClientRect().top - 12, behavior: 'smooth' });
      this.activeSection = i;
    },
    // 스크롤 중 현재 섹션 계산(rAF 로 1프레임 1회만)
    onDocScroll() {
      if (this._rafPending) return;
      this._rafPending = true;
      window.requestAnimationFrame(() => {
        this._rafPending = false;
        const tops = this.sectionTops();
        if (!tops.length) return;
        const body = this.$el.querySelector('.msds-prev__body');
        const line = (body ? body.getBoundingClientRect().top : 0) + 20; // 본문 상단을 지난 마지막 섹션 = '보고 있는 섹션'
        let idx = 0;
        tops.forEach((t, i) => {
          if (t <= line) idx = i;
        });
        const sc = this.scrollContainer();
        if (sc !== window && sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 4) idx = tops.length - 1; // 바닥이면 마지막 섹션
        this.activeSection = idx;
      });
    },
    // ===== [SECNAV] 끝 =====

    // 사유 모달에서 진입한 경우(startInEdit) iframe 준비되면 자동으로 편집모드 시작 + 사유 채움
    tryStartEdit() {
      if (!this.startInEdit || this.editing) return;
      const doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc || !doc.body) return;
      this.enterEdit();
      this.editReason = this.initialReason || '';
    },
    // ===== 편집 모드 =====
    // 본문 전체를 편집 영역으로(모든 텍스트·표 셀·혼합콘텐츠 일괄 편집), 이미지만 잠금.
    // 변경 추적: 마커(data-ef)를 **구조적 셀(td/th/li)** 에 단다.
    //   inline <span> 에 달면 contentEditable 이 편집 중 span 을 갈아치우며 마커가 날아가지만,
    //   td/th/li 셀은 편집 중에도 살아남아 마커가 보존된다. 원본 텍스트 스냅샷은 저장 시점 비교용.
    enterEdit() {
      const doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc) return;
      this._orig = {};
      this._changed = new Set();
      doc.body.setAttribute('contenteditable', 'true');
      doc.querySelectorAll('img').forEach(img => img.setAttribute('contenteditable', 'false'));
      let idx = 0;
      const norm = s => (s || '').replace(/\s+/g, ' ').trim();
      // 인라인 요소는 편집 중 브라우저가 갈아치워 마커가 소실되므로 제외하고, "최내곽 블록 텍스트 컨테이너"에만 마킹.
      // (td/th/li 뿐 아니라 섹션 제목 div/p/헤더 등도 포함 → 어디를 고치든 마커가 살아남음)
      const INLINE = new Set(['SPAN', 'B', 'I', 'EM', 'STRONG', 'A', 'SUP', 'SUB', 'U', 'FONT', 'BR', 'IMG', 'SMALL', 'LABEL', 'CODE', 'MARK']);
      const isInline = el => INLINE.has(el.tagName);
      const hasText = el => norm(el.textContent) !== '';
      const hasBlockChildWithText = el => Array.prototype.some.call(el.children, c => !isInline(c) && hasText(c));
      const hasBlockChild = el => Array.prototype.some.call(el.children, c => !isInline(c) && c.tagName !== 'SCRIPT' && c.tagName !== 'STYLE');
      doc.body.querySelectorAll('*').forEach(el => {
        if (isInline(el) || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return; // 블록 요소만
        if (hasBlockChildWithText(el)) return; // 하위에 텍스트 든 블록이 있으면 스킵 → 최내곽 블록만
        // 빈(공란) 셀도 추적 대상에 포함하되 '최내곽 빈 셀'만(빈 블록 자식이 있으면 그 자식이 최내곽) →
        // 공란 제품코드 등 빈 칸에 값을 입력해도 수정마커(data-ef)가 붙어 변경감지·노란 하이라이트가 동작.
        if (!hasText(el) && hasBlockChild(el)) return;
        const id = 'ef' + idx++;
        el.setAttribute('data-ef', id);
        this._orig[id] = norm(el.textContent);
      });
      const st = doc.createElement('style');
      st.id = 'ef-style';
      st.textContent = EDIT_STYLE;
      doc.head.appendChild(st);
      doc.addEventListener('input', this.onEditInput);
      doc.addEventListener('selectionchange', this.onEditSelection);
      this._active = null;
      this.changeCount = 0;
      this.editReason = '';
      this.reasonError = false;
      this.editing = true;
    },
    onEditInput() {
      // contentEditable 의 input 이벤트 target 은 편집 셀이 아니라 호스트(body) 이므로,
      // e.target 대신 캐럿 위치(selection)로 실제 편집된 [data-ef] 셀을 찾는다.
      const doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc) return;
      const sel = doc.getSelection();
      let node = sel && sel.focusNode ? sel.focusNode : null;
      if (node && node.nodeType === 3) node = node.parentElement;
      const el = node && node.closest ? node.closest('[data-ef]') : null;
      if (!el) return;
      const id = el.getAttribute('data-ef');
      const now = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (now !== this._orig[id]) {
        this._changed.add(id);
        el.setAttribute('data-efc', '1');
      } else {
        this._changed.delete(id);
        el.removeAttribute('data-efc');
      }
      this.changeCount = this._changed.size;
    },
    // 캐럿이 위치한 셀에 노란 배경(편집 중 표시)
    onEditSelection() {
      const doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc) return;
      const sel = doc.getSelection();
      let el = sel && sel.focusNode ? sel.focusNode : null;
      if (el && el.nodeType === 3) el = el.parentElement;
      el = el && el.closest ? el.closest('[data-ef]') : null;
      if (this._active === el) return;
      if (this._active) this._active.removeAttribute('data-efa');
      this._active = el;
      if (el) el.setAttribute('data-efa', '1');
    },
    // 편집 종료(원복) — 변경 텍스트 되돌리고 편집 속성 제거
    cleanupEdit(revert) {
      const doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (doc) {
        doc.removeEventListener('input', this.onEditInput);
        doc.removeEventListener('selectionchange', this.onEditSelection);
        doc.body.removeAttribute('contenteditable');
        doc.querySelectorAll('img').forEach(img => img.removeAttribute('contenteditable'));
        doc.querySelectorAll('[data-ef]').forEach(el => {
          const id = el.getAttribute('data-ef');
          if (revert && this._orig && id in this._orig) el.textContent = this._orig[id];
          el.removeAttribute('data-ef');
          el.removeAttribute('data-efc');
          el.removeAttribute('data-efa');
        });
        const st = doc.getElementById('ef-style');
        if (st) st.remove();
      }
      this._orig = {};
      this._changed = new Set();
      this._active = null;
      this.changeCount = 0;
      this.editing = false;
      this.editReason = '';
      this.reasonError = false;
    },
    cancelEdit() {
      this.cleanupEdit(true);
    },
    onBack() {
      if (this.editing) this.cleanupEdit(true);
      this.$emit('back');
    },
    // 정리된 편집 HTML 생성(수정 마커 확정 + 편집 잔재/유출 스타일 제거)
    buildEditedHtml() {
      const doc = this.$refs.frame.contentDocument;
      const clone = doc.documentElement.cloneNode(true);
      const norm = s => (s || '').replace(/\s+/g, ' ').trim();
      clone.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
      // 원본(enterEdit 스냅샷) 대비 텍스트가 바뀐 셀에만 영구 수정마커(data-edited). 저장 시점 비교라 편집 이벤트 유실에 견고.
      // 이전 발행본에서 온 data-edited 는 제거하지 않아 누적 보존 → 조회 시 백엔드가 [data-edited]에 노란 하이라이트.
      clone.querySelectorAll('[data-ef]').forEach(el => {
        const id = el.getAttribute('data-ef');
        if (this._orig[id] !== undefined && norm(el.textContent) !== this._orig[id]) {
          el.setAttribute('data-edited', 'true');
        }
        el.removeAttribute('data-ef');
        el.removeAttribute('data-efc');
        el.removeAttribute('data-efa');
      });
      // contentEditable 이 편집 하이라이트 배경을 인라인 style 로 흘리는 것 제거 → HTML/PDF 오염 방지.
      // 이 문서 템플릿은 배경을 항상 클래스(.sec, table.grid th)로만 주므로, 콘텐츠 요소의 인라인 background 는 전부 편집 유출 → 통째 제거.
      clone.querySelectorAll('[style]').forEach(el => {
        const s = el.getAttribute('style');
        const s2 = s.replace(/background(-color)?\s*:[^;]*;?/gi, '').trim();
        if (s2 !== s.trim()) {
          if (s2) el.setAttribute('style', s2);
          else el.removeAttribute('style');
        }
      });
      const ef = clone.querySelector('#ef-style');
      if (ef) ef.remove();
      const prev = clone.querySelector('#msds-preview-style'); // 백엔드 미리보기 전용(푸터 하단배치) → PDF엔 불필요
      if (prev) prev.remove();
      const edh = clone.querySelector('#msds-edited-style'); // 조회 전용 하이라이트 스타일 → 저장 시 제거(조회 때 백엔드가 재주입)
      if (edh) edh.remove();
      return '<!DOCTYPE html>' + clone.outerHTML;
    },
    saveEdit() {
      if (!this.editReason.trim()) {
        this.reasonError = true;
        return;
      }
      const html = this.buildEditedHtml();
      this.$emit('publishEdited', { html, reason: this.editReason.trim() });
    },
  },
  watch: {
    // 이미 열린 미리보기에서 수정 진입 시(사유 모달 후 startInEdit=true) 편집모드 시작
    startInEdit(v) {
      if (v) this.tryStartEdit();
    },
  },
  mounted() {
    this.fetchHtml();
    this.fetchConditions(); // [CONDPANEL]
    // [FIXHEAD] 창 크기 변경 시 높이 재계산 (되돌릴 때 이 2줄 삭제)
    this._onResize = () => this.fitViewport();
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    // [SECNAV] 스크롤 리스너 해제 (되돌릴 때 이 2줄 삭제)
    if (this._scEl) this._scEl.removeEventListener('scroll', this.onDocScroll);
    // [FIXHEAD] resize 리스너 해제 (되돌릴 때 이 1줄 삭제)
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    const doc = this.$refs.frame && this.$refs.frame.contentDocument;
    if (doc) {
      doc.removeEventListener('input', this.onEditInput);
      doc.removeEventListener('selectionchange', this.onEditSelection);
    }
  },
};
</script>

<style scoped>
.msds-prev {
  display: flex;
  flex-direction: column;
}
.msds-prev__bar {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
}
.msds-prev__title {
  font-size: 15px;
}
.msds-prev__action {
  font-size: 15px;
}
.msds-prev__divider {
  margin: 0 10px;
  color: #cfcfcf;
}
.msds-prev__chip {
  font-size: 12px;
  color: #555;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 2px 8px;
  margin-right: 6px;
}
.msds-prev__editbadge {
  font-size: 13px;
  color: #c77700;
  background: #fff3cd;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  padding: 2px 10px;
  display: inline-flex;
  align-items: center;
}
.msds-prev__reason {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  background: #fffbe6;
  border-bottom: 1px solid #f0e3a8;
}
.msds-prev__reason--err {
  background: #fdecea;
  border-bottom-color: #f5c2bd;
}
.msds-prev__reason-label {
  font-size: 13px;
  font-weight: bold;
  color: #555;
  white-space: nowrap;
}
.msds-prev__req {
  color: #d32f2f;
  margin-left: 2px;
}
.msds-prev__reason-input {
  flex: 1;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 13px;
  background: #fff;
  outline: none;
}
.msds-prev__reason--err .msds-prev__reason-input {
  border-color: #d32f2f;
}
/* ===== [SECNAV] 시작 — 되돌릴 때 이 블록 삭제 ===== */
.msds-prev__nav {
  display: flex;
  flex-wrap: nowrap;
  gap: 3px;
  flex: 0 1 auto;
  min-width: 0;
  margin: 0 8px 0 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
}
.msds-prev__navlabel {
  flex: 0 0 auto;
  font-size: 11px;
  color: #999;
  align-self: center;
  margin-right: 2px;
}
.msds-prev__navbtn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;
  font-size: 11px;
  color: #444;
  background: #fff;
  border: 1px solid #d8d8d8;
  border-radius: 3px;
  padding: 2px 6px;
  cursor: pointer;
  white-space: nowrap;
}
.msds-prev__navbtn:hover {
  border-color: #2e7d32;
  color: #2e7d32;
}
.msds-prev__navbtn.is-active {
  background: #2e7d32;
  border-color: #2e7d32;
  color: #fff;
}
.msds-prev__navnum {
  font-weight: bold;
  opacity: 0.7;
}
.msds-prev__navbtn.is-active .msds-prev__navnum {
  opacity: 1;
}
/* 마지막 섹션까지 스크롤로 올릴 수 있게 하는 하단 여백 */
.msds-prev__tail {
  height: 70vh;
}
/* ===== [SECNAV] 끝 ===== */
.msds-prev__body {
  background: #e9ebee;
  padding: 24px 0;
  min-height: 70vh;
}
/* ===== [CONDPANEL] 시작 — 되돌릴 때 이 블록 삭제 ===== */
.msds-prev__main {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}
.msds-prev__side {
  flex: 0 0 260px;
  width: 260px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e0e0e0;
  background: #fff;
  transition: flex-basis 0.15s, width 0.15s;
}
.msds-prev__side.is-collapsed {
  flex-basis: 30px;
  width: 30px;
}
.msds-prev__sidetoggle {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 6px 8px;
  background: #f5f5f5;
  border: 0;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  color: #444;
}
.msds-prev__sidetoggle:hover {
  background: #eee;
}
.msds-prev__sidebody {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px 20px;
}
.msds-prev__sidemsg {
  font-size: 12px;
  color: #888;
  padding: 8px 0;
}
.msds-prev__sgroup {
  padding: 8px 0;
  border-bottom: 1px dashed #eaeaea;
}
.msds-prev__slabel {
  font-size: 11px;
  font-weight: bold;
  color: #888;
  margin-bottom: 4px;
}
.msds-prev__sval {
  font-size: 13px;
  font-weight: bold;
  color: #222;
  word-break: break-all;
}
.msds-prev__tags {
  margin-top: 4px;
}
.msds-prev__tag {
  display: inline-block;
  font-size: 11px;
  border-radius: 3px;
  padding: 1px 6px;
  margin-right: 4px;
  border: 1px solid #ddd;
  color: #999;
}
.msds-prev__tag.on {
  background: #2e7d32;
  border-color: #2e7d32;
  color: #fff;
}
.msds-prev__srow {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #666;
  padding: 2px 0;
}
.msds-prev__srow b {
  color: #222;
  text-align: right;
  word-break: break-all;
}
.msds-prev__warn {
  color: #d32f2f;
  font-weight: bold;
  font-style: normal;
  margin-left: 3px;
}
.msds-prev__flag {
  font-size: 12px;
  padding: 3px 0;
  color: #999;
}
.msds-prev__flagcode {
  display: inline-block;
  min-width: 34px;
  font-weight: bold;
  color: #555;
}
.msds-prev__flag.on .msds-prev__flagcode {
  color: #2e7d32;
}
.msds-prev__flagstate {
  margin-right: 4px;
}
.msds-prev__flag.on .msds-prev__flagstate {
  color: #2e7d32;
  font-weight: bold;
}
.msds-prev__flagname {
  color: #666;
  font-size: 11px;
}
.msds-prev__flagnote {
  display: block;
  padding-left: 34px;
  font-size: 11px;
  color: #999;
  line-height: 1.3;
}
/* ===== [CONDPANEL] 끝 ===== */
/* ===== [FIXHEAD] 시작 — 되돌릴 때 이 블록 삭제 ===== */
/* 상단바 + 섹션바는 고정, 본문만 내부 스크롤 (전체 높이는 fitViewport 가 인라인으로 지정) */
.msds-prev__bar,
.msds-prev__reason,
.msds-prev__nav {
  flex: 0 0 auto;
}
.msds-prev__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
/* ===== [FIXHEAD] 끝 ===== */
.msds-prev__paper {
  display: block;
  margin: 0 auto;
  width: 820px;
  max-width: 95%;
  border: 0;
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.18);
}
.msds-prev__center {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #666;
}
</style>
